import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, Check, ArrowLeft, Loader2, Ticket, Send, CreditCard, HelpCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { api, formatApiError, absUploadUrl } from "../lib/apiClient";
import { OfferCard } from "./OffersList";
import { useI18n } from "../i18n/I18nProvider";
import AdyenCheckoutBlock from "../components/checkout/AdyenCheckoutBlock";

const mdComponents = {
  img: ({ node, ...props }) => (
    <img alt={props.alt || ""} {...props} src={absUploadUrl(props.src || "")} loading="lazy" />
  ),
};

export default function OfferDetail({ localeOverride }) {
  const { slug } = useParams();
  const { t, locale, setLocale } = useI18n();
  const effectiveLocale = localeOverride || locale;

  // If the URL forces FR (/offers/fr/...), sync the site locale to FR.
  useEffect(() => {
    if (localeOverride && localeOverride !== locale && setLocale) {
      setLocale(localeOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeOverride]);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [otherOffers, setOtherOffers] = useState([]);
  const [settings, setSettings] = useState({ payments_enabled: false });
  const [done, setDone] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const [{ data }, s] = await Promise.all([
          api.get(`/offers/${slug}?locale=${effectiveLocale}`),
          api.get("/offers/public-settings").catch(() => ({ data: { payments_enabled: false } })),
        ]);
        setOffer(data);
        setOtherOffers(data.others || []);
        setSettings(s.data || { payments_enabled: false });
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(err, "Could not load offer"));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, effectiveLocale]);

  // Inject <link rel="alternate" hreflang="..."> for the current offer
  useEffect(() => {
    if (!offer) return;
    const base = window.location.origin;
    const enHref = `${base}/offers/${offer.slug}`;
    const frHref = offer.slug_fr ? `${base}/offers/fr/${offer.slug_fr}` : null;
    const deHref = offer.slug_de ? `${base}/offers/de/${offer.slug_de}` : null;
    const links = [
      { hreflang: "en", href: enHref },
      ...(deHref ? [{ hreflang: "de", href: deHref }] : []),
      ...(frHref ? [{ hreflang: "fr", href: frHref }] : []),
      { hreflang: "x-default", href: enHref },
    ];
    const nodes = links.map((l) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = l.hreflang;
      el.href = l.href;
      el.dataset.ojOfferHreflang = "1";
      document.head.appendChild(el);
      return el;
    });
    // Canonical points to the URL that matches the currently shown language
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href =
      effectiveLocale === "fr" && frHref
        ? frHref
        : effectiveLocale === "de" && deHref
          ? deHref
          : enHref;
    canonical.dataset.ojOfferHreflang = "1";
    document.head.appendChild(canonical);
    return () => {
      document.querySelectorAll("link[data-oj-offer-hreflang]").forEach((n) => n.remove());
      nodes.forEach((n) => n.remove?.());
    };
  }, [offer, effectiveLocale]);

  if (loading) {
    return <div className="container-oj py-24 text-sm text-zinc-500">Loading...</div>;
  }
  if (notFound || !offer) {
    return <ExpiredOrNotFound message={t("offers.not_found")} />;
  }
  if (offer.expired) {
    return <ExpiredOrNotFound message={t("offers.expired_banner")} others={otherOffers} />;
  }

  const paymentsEnabled = !!settings.payments_enabled && Number(offer.price) > 0;

  return (
    <div data-testid="offer-detail-page" className="min-h-[60vh] bg-white">
      {/* Compact hero */}
      <section className="relative border-b border-zinc-200 bg-gradient-to-br from-zinc-50 to-white">
        <div className="container-oj py-10 md:py-14">
          <Link to="/offers" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-oj-ink mb-6">
            <ArrowLeft className="size-4" /> {t("offers.all_offers")}
          </Link>
          <div className="max-w-3xl">
            {offer.discount_label && (
              <div className="inline-flex items-center bg-oj-ink text-white text-xs font-bold px-3 py-1 rounded-full">
                {offer.discount_label}
              </div>
            )}
            <h1 data-testid="offer-title" className="mt-4 font-display font-black tracking-tighter text-oj-ink text-3xl sm:text-4xl lg:text-5xl leading-[0.98]">
              {offer.title}
            </h1>
            {offer.subtitle && (
              <p className="mt-3 text-lg text-zinc-600 max-w-2xl">{offer.subtitle}</p>
            )}
            <PriceDisplay offer={offer} />
            {offer.valid_until && (
              <p className="mt-3 text-xs text-zinc-500">
                {t("offers.valid_until").replace("{date}", new Date(offer.valid_until).toLocaleDateString())}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Banner */}
      {offer.banner_image_url && (
        <section className="bg-white">
          <div className="container-oj py-6 md:py-10">
            <img
              data-testid="offer-banner-image"
              src={absUploadUrl(offer.banner_image_url)}
              alt={offer.title}
              className="w-full rounded-2xl border border-zinc-200 object-cover aspect-[16/6]"
            />
          </div>
        </section>
      )}

      {/* Description */}
      <section className="bg-white">
        <div className="container-oj pt-4 pb-10 md:pt-6 md:pb-14 max-w-3xl">
          {offer.description && (
            <>
              <h2 className="text-2xl font-display font-bold text-oj-ink mb-4">{t("offers.details")}</h2>
              <div className="prose prose-zinc max-w-none prose-headings:font-display prose-headings:text-oj-ink prose-a:text-oj-brand prose-img:rounded-xl prose-img:my-6">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {offer.description}
                </ReactMarkdown>
              </div>
            </>
          )}
          {offer.terms && (
            <details className="mt-8 group rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <summary className="cursor-pointer text-sm font-semibold text-oj-ink">{t("offers.terms")}</summary>
              <div className="mt-3 whitespace-pre-line text-sm text-zinc-600 leading-relaxed">{offer.terms}</div>
            </details>
          )}
        </div>
      </section>

      {done ? (
        <SuccessCard />
      ) : (
        <>
          {/* PRIMARY ACTION — Buy directly, right under the details */}
          {paymentsEnabled && (
            <BuyDirectBlock offer={offer} onDone={() => setDone(true)} />
          )}

          {/* SECONDARY — questions / lead form */}
          <LeadFormSection
            offer={offer}
            onDone={() => setDone(true)}
            hasBuyBlock={paymentsEnabled}
          />
        </>
      )}

      {otherOffers.length > 0 && (
        <section className="bg-white border-t border-zinc-200">
          <div className="container-oj py-16">
            <h2 className="text-2xl font-display font-bold text-oj-ink mb-6">{t("offers.other_offers")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherOffers.slice(0, 6).map((o) => (
                <OfferCard key={o.id} offer={o} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function SuccessCard() {
  const { t } = useI18n();
  return (
    <section className="bg-zinc-50 border-t border-zinc-200">
      <div className="container-oj py-16 max-w-3xl">
        <div data-testid="offer-lead-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <Check className="size-6" />
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold text-emerald-900">{t("offers.success_title")}</h3>
          <p className="mt-2 text-sm text-emerald-800">{t("offers.success_body")}</p>
        </div>
      </div>
    </section>
  );
}

function PriceDisplay({ offer }) {
  const price = Number(offer.price) || 0;
  const original = Number(offer.original_price) || 0;
  const hasDiscount = original > price && price > 0;
  const savePct = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;
  if (price === 0 && !original) return null;
  return (
    <div data-testid="offer-price-display" className="mt-6 flex items-end gap-4 flex-wrap">
      {hasDiscount && (
        <span className="text-xl text-zinc-400 line-through" data-testid="offer-original-price">
          {offer.currency} {original.toFixed(2)}
        </span>
      )}
      {price > 0 ? (
        <span className="font-display font-black text-4xl sm:text-5xl text-oj-ink tracking-tighter" data-testid="offer-sale-price">
          {offer.currency} {price.toFixed(2)}
        </span>
      ) : (
        <span className="font-display font-black text-3xl text-emerald-600 tracking-tight" data-testid="offer-sale-price">
          Free
        </span>
      )}
      {(offer.saving_badge || hasDiscount) && (
        <span data-testid="offer-saving-badge" className="inline-flex items-center bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2">
          {offer.saving_badge || `Save ${savePct}%`}
        </span>
      )}
    </div>
  );
}

function ExpiredOrNotFound({ message, others }) {
  const { t } = useI18n();
  return (
    <div className="min-h-[60vh] bg-zinc-50">
      <div className="container-oj py-16">
        <div data-testid="offer-expired-banner" className="rounded-2xl border border-red-200 bg-red-50 p-6 flex items-start gap-3">
          <AlertCircle className="size-6 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">{message}</div>
            <Link to="/offers" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-red-800 hover:underline">
              {t("offers.check_others")} →
            </Link>
          </div>
        </div>
        {others && others.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {others.map((o) => (
              <OfferCard key={o.id} offer={o} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------- BUY DIRECTLY ------------------- */

function BuyDirectBlock({ offer, onDone }) {
  const { t, locale } = useI18n();
  const [contact, setContact] = useState({ full_name: "", email: "", phone: "", business_name: "" });
  const [voucher, setVoucher] = useState("");
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [starting, setStarting] = useState(false);

  const contactValid = !!(contact.full_name && contact.email && contact.phone);

  async function checkVoucher(code) {
    if (!code) return setVoucherStatus(null);
    try {
      const { data } = await api.post("/vouchers/validate", { code });
      setVoucherStatus(data);
    } catch {
      setVoucherStatus({ valid: false });
    }
  }

  const discountedPreview = React.useMemo(() => {
    const price = Number(offer.price) || 0;
    if (!voucherStatus || !voucherStatus.valid) return price;
    const val = Number(voucherStatus.value) || 0;
    if (voucherStatus.type === "percent") return Math.max(0, price - price * (val / 100));
    return Math.max(0, price - val);
  }, [voucherStatus, offer.price]);

  async function startPayment() {
    if (!contactValid) {
      toast.error(t("offers.fill_contact_first"));
      return;
    }
    setStarting(true);
    try {
      const { data } = await api.post(`/checkout/offer/${offer.slug}`, {
        contact,
        voucher_code: voucher || null,
        answers: {},
        locale,
      });
      setCheckoutData(data);
    } catch (err) {
      toast.error(formatApiError(err, "Payment could not start"));
    } finally {
      setStarting(false);
    }
  }

  return (
    <section id="buy" className="bg-oj-ink text-white scroll-mt-24" data-testid="buy-direct-section">
      <div className="container-oj py-14 max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-oj-brand text-oj-deep text-xs font-bold px-3 py-1.5 rounded-full">
            <CreditCard className="size-3.5" /> {t("offers.claim_this_offer")}
          </div>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tight">
            {t("offers.buy_direct_headline")}
          </h2>
          <p className="mt-3 text-white/70">{t("offers.buy_direct_sub")}</p>
        </div>

        {!checkoutData ? (
          <div className="rounded-2xl bg-white text-oj-ink p-6 md:p-8">
            <h3 className="text-sm uppercase tracking-wider font-semibold text-zinc-500 mb-3">
              {t("offers.your_details")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder={t("offers.full_name")} required value={contact.full_name} onChange={(v) => setContact({ ...contact, full_name: v })} testId="buy-full-name" />
              <Input type="email" placeholder={t("offers.email")} required value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testId="buy-email" />
              <Input placeholder={t("offers.phone")} required value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testId="buy-phone" />
              <Input placeholder={t("offers.business_name")} value={contact.business_name} onChange={(v) => setContact({ ...contact, business_name: v })} testId="buy-business" />
            </div>
            <div className="mt-4 pt-4 border-t border-zinc-100">
              <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1">
                <Ticket className="size-3.5" /> {t("offers.have_code")}
              </label>
              <input
                value={voucher}
                onChange={(e) => setVoucher(e.target.value)}
                onBlur={(e) => checkVoucher(e.target.value)}
                data-testid="buy-voucher"
                placeholder={t("offers.voucher_ph")}
                className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand"
              />
              {voucherStatus && (
                <p data-testid="buy-voucher-status" className={`mt-1 text-xs ${voucherStatus.valid ? "text-emerald-600" : "text-zinc-500"}`}>
                  {voucherStatus.valid
                    ? `✓ ${voucherStatus.description || `${voucherStatus.value}${voucherStatus.type === "percent" ? "%" : ` ${offer.currency}`} off`}`
                    : t("offers.voucher_invalid")}
                </p>
              )}
            </div>
            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">{t("offers.you_pay")}</div>
                <div className="mt-1 font-display font-black text-3xl text-oj-ink">
                  {offer.currency} {Number(discountedPreview).toFixed(2)}
                  {voucherStatus?.valid && Number(offer.price) > discountedPreview && (
                    <span className="ml-2 text-lg text-zinc-400 line-through font-normal">
                      {offer.currency} {Number(offer.price).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={startPayment}
                disabled={starting || !contactValid}
                data-testid="buy-now-btn"
                title={!contactValid ? t("offers.fill_contact_first") : ""}
                className="inline-flex items-center gap-2 bg-oj-ink text-white font-semibold px-5 py-3.5 rounded-md hover:bg-zinc-800 disabled:opacity-60"
              >
                {starting ? <Loader2 className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
                {t("offers.pay_now")}
              </button>
            </div>
            {!contactValid && (
              <p className="mt-3 text-xs text-zinc-500">{t("offers.fill_contact_first")}</p>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-white text-oj-ink p-6 md:p-8">
            <AdyenCheckoutBlock data={checkoutData} locale={locale} onDone={onDone} />
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------------- QUESTIONS / LEAD FORM ------------------- */

function LeadFormSection({ offer, onDone, hasBuyBlock }) {
  const { t, locale } = useI18n();
  const [contact, setContact] = useState({ full_name: "", email: "", phone: "", business_name: "" });
  const [voucher, setVoucher] = useState("");
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function setAnswer(qid, v) {
    setAnswers((a) => ({ ...a, [qid]: v }));
  }
  function toggleMulti(qid, opt) {
    setAnswers((a) => {
      const cur = Array.isArray(a[qid]) ? a[qid] : [];
      return { ...a, [qid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });
  }

  async function checkVoucher(code) {
    if (!code) return setVoucherStatus(null);
    try {
      const { data } = await api.post("/vouchers/validate", { code });
      setVoucherStatus(data);
    } catch {
      setVoucherStatus({ valid: false });
    }
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const answersOut = { ...answers };
      for (const q of offer.questions || []) {
        if (q.type === "file" && answers[q.id] instanceof File) {
          const fd = new FormData();
          fd.append("file", answers[q.id]);
          fd.append("question_id", q.id);
          const { data } = await api.post(`/offers/${offer.slug}/lead-file`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          answersOut[q.id] = data.url;
        }
      }
      await api.post(`/offers/${offer.slug}/lead`, {
        contact,
        voucher_code: voucher || null,
        answers: answersOut,
        locale,
      });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="ask" className="bg-zinc-50 border-t border-zinc-200 scroll-mt-24">
      <div className="container-oj py-14 max-w-3xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-oj-ink text-xs font-bold px-3 py-1.5 rounded-full">
            <HelpCircle className="size-3.5" /> {t("offers.ask_pill")}
          </div>
          <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl tracking-tight text-oj-ink">
            {hasBuyBlock ? t("offers.ask_headline") : t("offers.claim_headline_no_pay")}
          </h2>
          <p className="mt-3 text-zinc-600">
            {hasBuyBlock ? t("offers.ask_sub") : t("offers.claim_sub_no_pay")}
          </p>
        </div>

        <form onSubmit={submit} data-testid="offer-lead-form" className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder={t("offers.full_name")} required value={contact.full_name} onChange={(v) => setContact({ ...contact, full_name: v })} testId="lead-full-name" />
            <Input type="email" placeholder={t("offers.email")} required value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testId="lead-email" />
            <Input placeholder={t("offers.phone")} required value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testId="lead-phone" />
            <Input placeholder={t("offers.business_name")} value={contact.business_name} onChange={(v) => setContact({ ...contact, business_name: v })} testId="lead-business" />
          </div>

          {(offer.questions || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
              {(offer.questions || []).map((q) => (
                <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} onToggleMulti={(opt) => toggleMulti(q.id, opt)} />
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-zinc-100">
            <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1">
              <Ticket className="size-3.5" /> {t("offers.have_code")}
            </label>
            <input
              value={voucher}
              onChange={(e) => setVoucher(e.target.value)}
              onBlur={(e) => checkVoucher(e.target.value)}
              data-testid="lead-voucher"
              placeholder={t("offers.voucher_ph")}
              className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand"
            />
            {voucherStatus && (
              <p data-testid="voucher-status" className={`mt-1 text-xs ${voucherStatus.valid ? "text-emerald-600" : "text-zinc-500"}`}>
                {voucherStatus.valid
                  ? `✓ ${voucherStatus.description || `${voucherStatus.value}${voucherStatus.type === "percent" ? "%" : ` ${offer.currency}`} off`}`
                  : t("offers.voucher_invalid")}
              </p>
            )}
          </div>

          <button
            type="submit"
            data-testid="lead-submit"
            disabled={submitting}
            className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-oj-ink text-white font-semibold px-4 py-3.5 rounded-md hover:bg-zinc-800 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            {submitting ? t("offers.sending") : t("offers.submit")}
          </button>
          <p className="text-xs text-zinc-500 text-center mt-3">{t("offers.privacy_hint")}</p>
        </form>
      </div>
    </section>
  );
}

function Input({ type = "text", placeholder, required, value, onChange, testId }) {
  return (
    <input
      type={type}
      required={required}
      placeholder={placeholder}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:border-oj-brand"
    />
  );
}

function QuestionField({ q, value, onChange, onToggleMulti }) {
  const label = (
    <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-500">
      {q.label} {q.required && <span className="text-red-500">*</span>}
    </label>
  );
  const baseCls = "mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-oj-brand";

  if (q.type === "textarea") {
    return (
      <div>{label}
        <textarea data-testid={`q-${q.id}`} value={value || ""} required={q.required} onChange={(e) => onChange(e.target.value)} rows={3} className={baseCls} />
        {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
      </div>
    );
  }
  if (q.type === "number" || q.type === "date") {
    return (
      <div>{label}
        <input type={q.type === "number" ? "number" : "date"} data-testid={`q-${q.id}`} value={value || ""} required={q.required} onChange={(e) => onChange(e.target.value)} className={baseCls} />
        {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
      </div>
    );
  }
  if (q.type === "single_select") {
    return (
      <div>{label}
        <select data-testid={`q-${q.id}`} value={value || ""} required={q.required} onChange={(e) => onChange(e.target.value)} className={baseCls}>
          <option value="">—</option>
          {(q.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
      </div>
    );
  }
  if (q.type === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`q-${q.id}`}>
          {(q.options || []).map((opt) => {
            const active = arr.includes(opt);
            return (
              <button key={opt} type="button" onClick={() => onToggleMulti(opt)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-oj-ink text-white border-oj-ink" : "bg-white text-zinc-700 border-zinc-300 hover:border-oj-ink"}`}>
                {opt}
              </button>
            );
          })}
        </div>
        {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
      </div>
    );
  }
  if (q.type === "file") {
    return (
      <div>{label}
        <input type="file" data-testid={`q-${q.id}`} required={q.required} onChange={(e) => onChange(e.target.files?.[0])} className="mt-1 block text-sm w-full" />
        {value instanceof File && <p className="text-xs text-emerald-600 mt-1">{value.name}</p>}
        {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
      </div>
    );
  }
  return (
    <div>{label}
      <input data-testid={`q-${q.id}`} value={value || ""} required={q.required} onChange={(e) => onChange(e.target.value)} className={baseCls} />
      {q.help_text && <p className="text-xs text-zinc-500 mt-1">{q.help_text}</p>}
    </div>
  );
}
